import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

interface XlsDownloadRequest {
  stratumSizesCalculated?: boolean;
}

interface XlsDownloadMutationOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export function useXlsDownload(options?: XlsDownloadMutationOptions) {
  return useMutation({
    mutationFn: async (data: XlsDownloadRequest) => {
      try {
        // Make POST request to get the file directly
        const response = await axios.post('/forecast-stratum/sampling-rates-xls', {}, {
          responseType: 'blob',
        });

        let fileName: string | null = null;
        
        // Try to extract the filename from the Content-Disposition header
        const contentDisposition = 
          response.headers['content-disposition'] || 
          response.headers['Content-Disposition'];
        
        if (contentDisposition) {
          // Match the filename in Content-Disposition header
          const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
          if (filenameMatch && filenameMatch[2]) {
            fileName = decodeURIComponent(filenameMatch[2]);
          }
        }
        
        // If we couldn't get the filename from the header, generate one
        if (!fileName) {
          // Extract the current date in YYYYMMDD format for filename
          const date = new Date();
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const timestamp = String(date.getHours()).padStart(2, '0') + 
                            String(date.getMinutes()).padStart(2, '0') + 
                            String(date.getSeconds()).padStart(2, '0');
          
          // Format filename for XLS file
          fileName = `Forecast_Stratum_Sampling_Rates_${year}${month}${day}_${timestamp}.xlsx`;
        }
        
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading XLS file:', error);
        throw error;
      }
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

export type { XlsDownloadRequest };
